import { ChangeEvent, memo, useCallback, useRef, useState } from 'react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { preloadAttachments } from '@/modules/api';
import { PreloadAttachmentsResponse } from '@/modules/api/types';
import { Spinner } from '@/modules/common/components/spinner';
import { Button, HiddenInput } from './UploadButton.styles';

export interface UploadButtonProps {
  disabled?: boolean;
  onFileSelect?: (files: PreloadAttachmentsResponse) => void;
}

export const UploadButton = memo<UploadButtonProps>((props) => {
  const { disabled, onFileSelect } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        setIsLoading(true);
        try {
          const response = await preloadAttachments(Array.from(files));
          onFileSelect?.(response);
        } catch (e) {
          console.error('Error while uploading file', e);
        } finally {
          setIsLoading(false);
        }
      }
      e.target.value = '';
    },
    [onFileSelect]
  );

  return (
    <>
      <Button
        type="button"
        disabled={disabled || isLoading}
        onClick={handleClick}
        role="button"
        aria-label="upload file"
        data-testid="saywhatt-chat-upload-button"
      >
        {isLoading ? <Spinner size={14} color="#666" /> : <PlusIcon />}
      </Button>
      <HiddenInput ref={inputRef} type="file" accept=".png, .jpg, .jpeg" onChange={handleChange} />
    </>
  );
});

UploadButton.displayName = 'UploadButton';
